import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useResetPassword } from '../hooks/useAuth';

const StyledCard = styled(Card)(() => ({
  maxWidth: '100%',
  width: '95%',
  '@media (min-width: 600px)': {
    width: '80%',
  },
  '@media (min-width: 900px)': {
    width: '70%',
  },
  '@media (min-width: 1200px)': {
    width: '60%',
  },
  '@media (min-width: 1536px)': {
    width: '50%',
  },
  margin: '0 auto',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  borderRadius: 16,
}));

const GradientBackground = styled(Box)(() => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, 
    #8b9cff15 0%, 
    #ff6b9d10 50%,
    #ffffff 100%)`,
}));

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  
  const resetPassword = useResetPassword();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('No reset token provided');
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!token) {
      setError('No reset token available');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await resetPassword.mutateAsync({
        token,
        new_password: formData.password
      });
      setSuccess(true);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to reset password');
    }
  };

  if (success) {
    return (
      <GradientBackground>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <StyledCard>
            {/* Gradient Header Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                color: 'white',
                textAlign: 'center',
                py: 3,
                px: 4,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.1), transparent)',
                }
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                Password Reset
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
                Your password has been updated
              </Typography>
            </Box>
            
            {/* Content Section */}
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" color="success.main" gutterBottom>
                ✓ Password Reset Successfully!
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Your password has been updated successfully. You can now log in with your new password.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7a8bff 0%, #ff5a8d 100%)',
                  },
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                }}
              >
                Continue to Login
              </Button>
            </CardContent>
          </StyledCard>
        </Container>
      </GradientBackground>
    );
  }

  return (
    <GradientBackground>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <StyledCard>
          {/* Gradient Header Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
              color: 'white',
              textAlign: 'center',
              py: 3,
              px: 4,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '20px',
                background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.1), transparent)',
              }
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Reset Password
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
              Enter your new password
            </Typography>
          </Box>
          
          {/* Content Section */}
          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your new password"
                helperText="Password must be at least 8 characters long"
                sx={{ mb: 3 }}
              />
              
              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirm your new password"
                sx={{ mb: 3 }}
              />
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={resetPassword.isPending || !token}
                sx={{
                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7a8bff 0%, #ff5a8d 100%)',
                  },
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                }}
              >
                {resetPassword.isPending ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
              
              <Box textAlign="center">
                <Link component={RouterLink} to="/login" variant="body1" sx={{ color: '#8b9cff', textDecoration: 'none' }}>
                  Back to Login
                </Link>
              </Box>
            </Box>
          </CardContent>
        </StyledCard>
      </Container>
    </GradientBackground>
  );
}

export default ResetPassword;




